import { useCallback, useEffect, useState } from 'react';
import { api } from './api';

/**
 * Minimal data-fetching hook.
 *
 * Deliberately not a data-fetching library: the console has modest needs and
 * one small, auditable hook keeps the dependency surface small.
 *
 * The whole request lifecycle lives in a single state object so the effect
 * performs exactly one state transition per outcome rather than a cascade of
 * synchronous `setState` calls.
 */
export interface Resource<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
}

interface FetchState<T> {
  data: T | null;
  error: unknown;
  loading: boolean;
  /** The path the current data belongs to, so a changed path shows as loading. */
  path: string | null;
}

export function useResource<T>(path: string | null): Resource<T> {
  const [state, setState] = useState<FetchState<T>>({
    data: null,
    error: null,
    loading: false,
    path: null,
  });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (path === null) return;
    let cancelled = false;

    api
      .get<T>(path)
      .then((result) => {
        if (!cancelled) setState({ data: result, error: null, loading: false, path });
      })
      .catch((cause) => {
        if (!cancelled) setState({ data: null, error: cause, loading: false, path });
      });

    return () => {
      cancelled = true;
    };
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  // Derived, not stored: a request is in flight whenever the settled path does
  // not match the requested one.
  const loading = path !== null && state.path !== path;

  return {
    data: state.path === path ? state.data : null,
    error: state.path === path ? state.error : null,
    loading,
    reload,
  };
}

/** Local form state helper with a typed patch function. */
export function useForm<T extends Record<string, unknown>>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const set = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setValues((current) => ({ ...current, [key]: value }));
  }, []);
  const reset = useCallback(() => setValues(initial), [initial]);
  return { values, set, setValues, reset };
}
